/**
 * Navbar controller: dropdown disclosures, the searchable city switcher, the mobile hamburger, and click logging.
 *
 * Vanilla + self-contained: the navbar's interactivity lives here, independent of any third-party plugin.
 * Menus follow the WAI-ARIA disclosure pattern (a button with aria-expanded controlling a panel of links) rather
 * than the application-menu pattern, which is the recommended model for site navigation and lets the city panel
 * hold a search field. Loaded as a deferred script on every page that renders the navbar (via main.scala.html).
 */
export class NavbarController {
  /** @type {HTMLElement} The <nav id="header"> root. */
  #nav;

  /** @type {HTMLButtonElement[]} All dropdown toggle buttons. */
  #toggles = [];

  /** @type {?{li: HTMLElement, btn: HTMLButtonElement}} The currently open dropdown, or null. */
  #current = null;

  /** @type {?HTMLElement} The <div id="navbar"> holding both nav groups. */
  #menu = null;

  /** @type {?HTMLElement} The hamburger button; its visibility tells us the nav is in its stacked state. */
  #hamburger = null;

  /** @type {HTMLElement[]} The two nav groups (primary, utility) whose combined width has to fit the bar. */
  #navGroups = [];

  /** @type {?HTMLElement} The strip in the brand area that holds the collapsed bar's quick links. */
  #quickStrip = null;

  /** @type {?boolean} Whether the last fit found the nav collapsed; null before the first one. */
  #stacked = null;

  /**
   * The `data-nav-quick` items in authored order, which is the order the collapsed bar shows them in — destinations
   * then the account control, matching the inline bar's primary-then-utility reading order.
   * @type {Array<{li: HTMLElement, parent: HTMLElement}>}
   */
  #quickItems = [];

  /**
   * The same items ordered by `data-nav-quick`, lowest first. This is a priority ranking, not a layout order: the bar
   * drops from the back of it when the row won't fit, so the account control survives to the narrowest widths.
   * @type {Array<{li: HTMLElement, parent: HTMLElement}>}
   */
  #quickByPriority = [];

  /**
   * Each quick item's original list, in its original child order, which is what says where an item goes back to.
   * Recorded per list rather than as a "next sibling" per item, because that sibling can be a quick item that is
   * itself away in the strip.
   * @type {Map<HTMLElement, Element[]>}
   */
  #quickHomes = new Map();

  /**
   * Space-freeing steps for the inline bar, ordered lowest priority first. Each takes a boolean and applies or
   * clears its effect; #fitNav applies as few as will make the bar fit.
   * @type {Array<(apply: boolean) => void>}
   */
  #shedSteps = [];

  /**
   * Click-target id → activity string logged for that navbar element.
   * @type {Record<string, string>}
   */
  #logMap = {
    'navbar-brand': 'Click_module=PSLogo',
    'navbar-sign-in-btn': 'Click_module=SignIn',
    'navbar-start-btn': 'Click_module=StartExploring',
    'navbar-validate-btn': 'Click_module=StartValidating',
    'navbar-guide-btn': 'Click_module=LabelingGuide',
    'navbar-retake-tutorial-btn': 'Click_module=RetakeTutorial',
    'navbar-api-btn': 'Click_module=SidewalkAPI',
    'navbar-cities-btn': 'Click_module=DeploymentSitesDashboard',
    'navbar-gallery-btn': 'Click_module=Gallery',
    'navbar-expert-validate-btn-tools': 'Click_module=ExpertValidate_from=ToolsDropdown',
    'navbar-leaderboard-btn': 'Click_module=Leaderboard',
    'navbar-leaderboard-btn-user': 'Click_module=Leaderboard_from=UserDropdown',
    'navbar-labelmap-btn': 'Click_module=LabelMap',
    'navbar-labelmap-btn-tools': 'Click_module=LabelMap_from=ToolsDropdown',
    'navbar-route-builder-btn': 'Click_module=RouteBuilder',
    'navbar-sign-out-btn': 'Click_module=SignOut',
    'navbar-admin-btn': 'Click_module=ToAdmin',
    'navbar-dashboard-btn': 'Click_module=ToDashboard',
    'navbar-your-stories-btn': 'Click_module=YourStories_from=UserDropdown',
    'navbar-your-routes-btn': 'Click_module=YourRoutes_from=UserDropdown',
    'navbar-expert-validate-btn-user': 'Click_module=ExpertValidate_from=UserDropdown',
  };

  constructor() {
    this.#nav = document.getElementById('header');
    if (!this.#nav) return;
    this.#wireDropdowns();
    this.#wireHamburger();
    this.#wireCitySearch();
    this.#wireGlobalHandlers();
    this.#wireLogging();
    this.#wireResponsiveNav();
  }

  /**
   * Returns the visible, focusable links/buttons inside a panel (excludes the city search input and hidden items).
   * @param {HTMLElement} panel - The dropdown panel.
   * @returns {HTMLElement[]} Focusable items in DOM order.
   */
  #items(panel) {
    return Array.from(panel.querySelectorAll('a[href], button:not([disabled])'))
      .filter((el) => el.offsetParent !== null);
  }

  /**
   * Opens a dropdown, closing any other open one first.
   * @param {HTMLElement} li - The <li> wrapping the toggle + panel.
   * @param {HTMLButtonElement} btn - The toggle button.
   * @param {HTMLElement} panel - The panel to reveal.
   * @param {boolean} [focusFirst=false] - Move focus into the panel (search input if present, else first item).
   */
  #open(li, btn, panel, focusFirst = false) {
    this.#closeAll({ except: li });
    // A dropdown outside the collapsible panel is a peer of it, not a child, so the two can't both be showing. The
    // ones still inside it (Tools, city, language) obviously must leave it open.
    if (this.#menu && !this.#menu.contains(li)) this.#setMenuOpen(false);
    li.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
    this.#current = { li, btn };
    // Only move focus into the panel on a keyboard-initiated open; a mouse/touch open leaves focus on the toggle so
    // it doesn't steal focus into the city search (which would pop the on-screen keyboard on mobile).
    if (!focusFirst) return;
    const search = panel.querySelector('.nav-city-search');
    if (search) {
      search.focus();
    } else {
      const items = this.#items(panel);
      if (items[0]) items[0].focus();
    }
  }

  /**
   * Closes a dropdown.
   * @param {HTMLElement} li - The <li> wrapping the toggle + panel.
   * @param {HTMLButtonElement} btn - The toggle button.
   * @param {boolean} [focusBtn=false] - Return focus to the toggle button (used for keyboard close).
   */
  #close(li, btn, focusBtn = false) {
    li.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    if (this.#current && this.#current.li === li) this.#current = null;
    if (focusBtn) btn.focus();
  }

  /**
   * Closes every open dropdown, optionally sparing one or limiting the sweep to a container.
   *
   * @param {object} [opts] - Options.
   * @param {?HTMLElement} [opts.except] - A <li> to leave open.
   * @param {?HTMLElement} [opts.within] - Close only dropdowns inside this element; all of them when omitted.
   */
  #closeAll({ except = null, within = null } = {}) {
    for (const btn of this.#toggles) {
      const li = btn.closest('.navbar-item');
      if (!li || li === except || !li.classList.contains('is-open')) continue;
      if (within && !within.contains(li)) continue;
      this.#close(li, btn);
    }
  }

  /** Wires click + keyboard behavior on each dropdown toggle and its panel. */
  #wireDropdowns() {
    this.#toggles = Array.from(this.#nav.querySelectorAll('[data-nav-dropdown]'));
    for (const btn of this.#toggles) {
      const li = btn.closest('.navbar-item');
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!li || !panel) continue;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (li.classList.contains('is-open')) this.#close(li, btn, true);
        // e.detail === 0 marks a keyboard-activated click (Enter/Space); move focus into the panel then, but not on
        // a real mouse/touch click (whose detail is >= 1) so tapping doesn't pop the mobile keyboard.
        else this.#open(li, btn, panel, e.detail === 0);
      });

      btn.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (li.classList.contains('is-open')) {
            const items = this.#items(panel);
            if (items[0]) items[0].focus();
          } else {
            this.#open(li, btn, panel, true);
          }
        } else if (e.key === 'Escape' && li.classList.contains('is-open')) {
          e.preventDefault();
          this.#close(li, btn, true);
        }
      });

      panel.addEventListener('keydown', (e) => this.#onPanelKeydown(e, li, btn, panel));
    }
  }

  /**
   * Handles roving focus + Escape within an open panel.
   * @param {KeyboardEvent} e - The keydown event.
   * @param {HTMLElement} li - The wrapping <li>.
   * @param {HTMLButtonElement} btn - The toggle button.
   * @param {HTMLElement} panel - The panel.
   */
  #onPanelKeydown(e, li, btn, panel) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.#close(li, btn, true);
      return;
    }
    const items = this.#items(panel);
    const search = panel.querySelector('.nav-city-search');
    // In the search field, only ArrowDown (jump to first result) is intercepted; typing is left alone.
    if (e.target === search) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (items[0]) items[0].focus();
      }
      return;
    }
    const idx = items.indexOf(/** @type {HTMLElement} */ (e.target));
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (items[idx + 1]) items[idx + 1].focus();
      else if (search) search.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx > 0) items[idx - 1].focus();
      else if (search) search.focus();
      else btn.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      if (items[0]) items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      if (items.length) items[items.length - 1].focus();
    }
  }

  /** Wires the mobile hamburger toggle for the collapsed nav. */
  #wireHamburger() {
    this.#hamburger = this.#nav.querySelector('[data-nav-toggle]');
    this.#menu = document.getElementById('navbar');
    if (!this.#hamburger || !this.#menu) return;
    this.#hamburger.addEventListener('click', () => {
      this.#setMenuOpen(!this.#menu.classList.contains('is-open'));
    });
  }

  /**
   * Opens or closes the collapsed nav's panel.
   *
   * Opening it closes any open dropdown, because the panel and the quick strip's dropdowns are peer disclosures
   * anchored to the same bar: with the account control promoted into the strip, both could otherwise be open at once
   * and overlap each other. Closing it closes the ones nested inside it, since `is-open` lives on the <li> and nothing
   * else clears it — an expanded Tools menu would come back expanded, and render as a floating panel in the inline bar
   * if the window widened first.
   *
   * @param {boolean} open - Whether the panel should be open.
   */
  #setMenuOpen(open) {
    if (!this.#menu) return;
    this.#closeAll(open ? {} : { within: this.#menu });
    this.#menu.classList.toggle('is-open', open);
    this.#hamburger?.setAttribute('aria-expanded', String(open));
  }

  /**
   * Sets up the progressive shedding that keeps the bar on one line, and re-runs it whenever the available width
   * can have changed.
   */
  #wireResponsiveNav() {
    if (!this.#menu) return;
    this.#navGroups = Array.from(this.#menu.querySelectorAll('#navbar > .navbar-list'));

    this.#quickStrip = document.getElementById('navbar-quick');
    this.#quickItems = Array.from(this.#menu.querySelectorAll('[data-nav-quick]'))
      .map((li) => ({ li, parent: li.parentElement }));
    this.#quickByPriority = [...this.#quickItems]
      .sort((a, b) => Number(a.li.dataset.navQuick) - Number(b.li.dataset.navQuick));
    for (const { parent } of this.#quickItems) {
      if (!this.#quickHomes.has(parent)) this.#quickHomes.set(parent, Array.from(parent.children));
    }

    // Items opt in via data-nav-shed="<n>", n ascending from the first to be dropped.
    this.#shedSteps = Array.from(this.#menu.querySelectorAll('[data-nav-shed]'))
      .sort((a, b) => Number(a.dataset.navShed) - Number(b.dataset.navShed))
      .map((li) => (on) => li.classList.toggle('is-shed', on));

    const lang = document.getElementById('language-dropdown');
    if (lang) this.#shedSteps.push((on) => lang.classList.toggle('is-compact', on));

    let queued = false;
    const update = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.#fitNav();
      });
    };
    window.addEventListener('resize', update);
    // Web fonts land after first paint and change every label's width, so re-fit once they're in.
    if (document.fonts) document.fonts.ready.then(update);
    this.#fitNav();
  }

  /**
   * Keeps the inline bar on a single line: restores every item, then applies shed steps in priority order until the
   * two nav groups fit. Measured at runtime rather than pinned to breakpoints, because the widths involved depend on
   * the active language's labels and on the signed-in username.
   */
  #fitNav() {
    // In the stacked hamburger panel each item has its own row, so everything is shown.
    const stacked = Boolean(this.#hamburger && this.#hamburger.offsetParent !== null);

    // Crossing the collapse boundary restyles every disclosure in the bar: the panel is the inline bar on the far
    // side, and a dropdown is an anchored popover on one side and a flattened row on the other. Anything open at that
    // moment was opened against the layout being left, so it closes. Only on the crossing — a plain resize (an
    // on-screen keyboard opening, a URL bar collapsing) must not yank a menu out from under the user mid-use.
    if (this.#stacked !== null && stacked !== this.#stacked) {
      this.#setMenuOpen(false);
      this.#closeAll();
    }
    this.#stacked = stacked;

    for (const step of this.#shedSteps) step(false);
    this.#applyQuickStrip(stacked);
    if (stacked) return;
    for (const step of this.#shedSteps) {
      if (this.#navFits()) return;
      step(true);
    }
  }

  /**
   * Moves the `data-nav-quick` items between the collapsed bar's quick strip and their places in the inline bar.
   *
   * Below the collapse breakpoint the bar is otherwise just a logo and a hamburger, which buries the handful of
   * destinations that are worth one tap. Each item is moved rather than duplicated — one copy keeps the ids the
   * click logging and the dialog wiring key on unique — so an item promoted to the strip leaves the panel, and one
   * that doesn't fit stays in the panel. Nothing is ever unreachable.
   *
   * @param {boolean} stacked - Whether the nav is collapsed behind the hamburger.
   */
  #applyQuickStrip(stacked) {
    if (!this.#quickStrip) return;

    if (!stacked) {
      for (const item of this.#quickItems) this.#sendHome(item);
      return;
    }

    for (const { li } of this.#quickItems) {
      if (li.parentElement !== this.#quickStrip) this.#closeOpenDropdown(li);
      this.#quickStrip.appendChild(li);
      li.classList.add('is-quick');
    }
    // Drop the lowest-priority items until the row fits. Measured rather than pinned to a second breakpoint, because
    // the widths involved depend on the active language's labels, the per-city logo, and which items the page renders.
    for (const item of [...this.#quickByPriority].reverse()) {
      if (this.#quickStripFits()) return;
      this.#sendHome(item);
    }
  }

  /**
   * Puts a quick item back in its own list, before the first of its original later siblings that is currently there.
   * Any that are still in the strip are skipped, so the surviving items keep their authored order however many of
   * them have been promoted.
   *
   * @param {{li: HTMLElement, parent: HTMLElement}} item - The item and the list it belongs to.
   */
  #sendHome({ li, parent }) {
    li.classList.remove('is-quick');
    if (li.parentElement === parent) return;
    this.#closeOpenDropdown(li);
    const order = this.#quickHomes.get(parent) || [];
    const anchor = order.slice(order.indexOf(li) + 1).find((el) => el.parentElement === parent) || null;
    parent.insertBefore(li, anchor);
  }

  /**
   * Closes an item's dropdown, if it has one open, before the item changes lists.
   *
   * The open state is a class on the <li> and `.navbar-item.is-open > .navbar-menu` isn't scoped by breakpoint, so an
   * item carried across the collapse boundary still open would render its panel in a bar the user never opened it
   * from — floating and unanchored in the inline bar, with no gesture behind it.
   *
   * @param {HTMLElement} li - The item about to move.
   */
  #closeOpenDropdown(li) {
    if (!li.classList.contains('is-open')) return;
    const btn = /** @type {HTMLButtonElement} */ (li.querySelector('[data-nav-dropdown]'));
    if (btn) this.#close(li, btn);
  }

  /**
   * @returns {boolean} Whether the brand row (logo + quick strip + hamburger) fits on one line.
   */
  #quickStripFits() {
    const row = this.#quickStrip.parentElement;
    // The strip is the flex item that gives, so the row's own scrollWidth stays pinned to its width; sum the
    // children instead. The slack keeps the logo and the links from sitting flush against each other.
    const needed = Array.from(row.children)
      .reduce((sum, child) => sum + child.getBoundingClientRect().width, 0);
    return needed <= this.#visibleWidth(row) - 16;
  }

  /**
   * The width of an element that the viewer can actually see.
   *
   * The navbar is fixed to the initial containing block, which a horizontally overflowing page widens past the
   * screen — so a bar that measures as fitting can still run its last item off the right edge. Anything past the
   * visible edge doesn't count toward the budget. Reduces to clientWidth on a page that doesn't overflow.
   *
   * @param {HTMLElement} el - The element being measured.
   * @returns {number} Its width in px, less however much of it sits beyond the viewport's right edge.
   */
  #visibleWidth(el) {
    const overhang = Math.max(0, el.getBoundingClientRect().right - document.documentElement.clientWidth);
    return el.clientWidth - overhang;
  }

  /**
   * @returns {boolean} Whether both nav groups currently fit side by side on one line.
   */
  #navFits() {
    // #navbar is justify-content: space-between, so its children are spread to span the full width no matter how few
    // remain — scrollWidth therefore reports the container's own width and can't reveal how much room is left. Sum the
    // groups' own widths instead; they're flex: none, so each is already its intrinsic width.
    const needed = this.#navGroups.reduce((sum, group) => sum + group.getBoundingClientRect().width, 0);

    // Slack covers the gap between the groups plus a little breathing room, so items never sit flush together.
    return needed <= this.#visibleWidth(this.#menu) - 24;
  }

  /** Wires live client-side filtering of the city switcher, hiding empty country groups. */
  #wireCitySearch() {
    const input = /** @type {HTMLInputElement} */ (document.getElementById('nav-city-search'));
    const menu = document.getElementById('nav-city-menu');
    if (!input || !menu) return;
    const empty = menu.querySelector('.nav-city-empty');
    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      let anyVisible = false;
      for (const item of menu.querySelectorAll('.nav-city-item')) {
        const match = !query || (item.dataset.search || '').includes(query);
        item.hidden = !match;
        if (match) anyVisible = true;
      }
      for (const group of menu.querySelectorAll('.nav-city-group')) {
        group.hidden = !group.querySelector('.nav-city-item:not([hidden])');
      }
      if (empty) empty.hidden = anyVisible;
    });
  }

  /** Closes the open dropdown on an outside click or when focus leaves it (e.g. Tab-out). */
  #wireGlobalHandlers() {
    /** @param {EventTarget} target - Where the click or focus landed. */
    const closeIfOutside = (target) => {
      if (this.#current && !this.#current.li.contains(/** @type {Node} */ (target))) {
        this.#close(this.#current.li, this.#current.btn);
      }
    };
    document.addEventListener('click', (e) => closeIfOutside(e.target));

    // The panel dismisses on press, not click: closing a dropdown reflows the panel between press and release, which
    // moves the row out from under the finger and makes the browser retarget the click to a common ancestor. Read on
    // click, that ancestor looks like an outside tap and would close the panel the user is still using. The hamburger
    // is excluded — its own handler has already toggled, and closing here would undo an open just asked for.
    document.addEventListener('pointerdown', (e) => {
      if (!this.#menu?.classList.contains('is-open')) return;
      const target = /** @type {Node} */ (e.target);
      if (this.#menu.contains(target) || this.#hamburger?.contains(target)) return;
      this.#setMenuOpen(false);
    });

    // Only a focus move out of the navbar entirely closes the open dropdown. Focus landing on another navbar control
    // is left alone: that control's own click/keydown handler closes the others via #open, and closing here would
    // reflow the bar mid-gesture, on the mousedown before the click that opens the next one lands.
    document.addEventListener('focusin', (e) => {
      if (!this.#nav.contains(/** @type {Node} */ (e.target))) closeIfOutside(e.target);
    });

    // Escape closes the panel, innermost disclosure first: a dropdown inside it consumes the key (its handlers call
    // preventDefault), so the first Escape closes that dropdown and only the next one closes the panel.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (!this.#menu?.classList.contains('is-open')) return;
      this.#setMenuOpen(false);
      this.#hamburger?.focus();
    });
  }

  /** Logs navbar clicks to the activity table via a single delegated listener keyed on element id. */
  #wireLogging() {
    const knownSelector = Object.keys(this.#logMap).map((id) => `#${id}`).join(',');
    this.#nav.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      const cityLink = e.target.closest('#nav-city-menu a[href]');
      if (cityLink && !cityLink.closest('.nav-city-current')) {
        this.#log(`Click_module=NavbarCityDropdown_city=${cityLink.id}`);
        return;
      }
      const known = e.target.closest(knownSelector);
      if (known && this.#logMap[known.id]) this.#log(this.#logMap[known.id]);
    });
  }

  /**
   * Sends one activity log line in the navbar's established format.
   * @param {string} activity - The Click_module=... prefix.
   */
  #log(activity) {
    if (typeof window.logWebpageActivity !== 'function') return;
    window.logWebpageActivity(`${activity}_location=Navbar_route=${window.location.pathname}`);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new NavbarController());
} else {
  new NavbarController();
}
